export type GridAddMoveMode = 'add' | 'move';

export const GRID_ADD_MODE: GridAddMoveMode = 'add';
export const GRID_MOVE_MODE: GridAddMoveMode = 'move';

const SUPPORTED_GRID_MODES = new Set([
  'Point',
  'Line',
  'Ray',
  'Segment',
  'Shade',
  'Parabola',
  'Highlight Point',
]);
const DRAGGABLE_GRID_SHAPE_TYPES = new Set([
  'Line',
  'Ray',
  'Segment',
  'Parabola',
]);
const GRID_LINE_STYLE_MODES = new Set(['Line', 'Ray', 'Segment']);

interface CanUseGridMoveModeOptions {
  mode: GridAddMoveMode;
  status?: string;
  actingAsNumberLineGetter?: boolean;
  gridGuideOpen?: boolean;
  gridGuideIgnoringMouseDown?: boolean;
}

interface CanUseGridAddControlsOptions {
  selected?: boolean;
  status?: string;
  moveModeActive?: boolean;
  visible?: boolean;
}

interface ConfigureGridAddMoveModeOptions {
  getter: {
    gridAddMoveMode?: GridAddMoveMode;
    isGridMoveModeActive?: () => boolean;
    canUseGridMoveMode?: () => boolean;
    setGridAddMoveMode?: (mode: GridAddMoveMode) => void;
    updateUndoRedoButtons?: () => void;
    st?: string;
    actingAsNumberLineGetter?: boolean;
  };
  getGridGuide: () =>
    | {
        on?: boolean;
        ignoreNextGridMouseDown?: boolean;
      }
    | undefined;
  onAddModeActivated?: () => void;
  paintCanvas: () => void;
}

export const isSupportedGridMode = (mode: string): boolean =>
  SUPPORTED_GRID_MODES.has(mode);

export const isDraggableGridShapeType = (mode: string | undefined): boolean =>
  Boolean(mode && DRAGGABLE_GRID_SHAPE_TYPES.has(mode));

export const isDraggableGridGetterMode = (mode: string | undefined): boolean =>
  mode === 'Point' || isDraggableGridShapeType(mode);

export const isGridLineStyleMode = (mode: string | undefined): boolean =>
  Boolean(mode && GRID_LINE_STYLE_MODES.has(mode));

export const getGridModeSelectorSizerText = (modes: string[]): string =>
  modes
    .filter(isSupportedGridMode)
    .reduce(
      (longest, mode) => (mode.length > longest.length ? mode : longest),
      ''
    );

export const getStandardGridModeSelectorSizerText = (): string =>
  getGridModeSelectorSizerText(Array.from(SUPPORTED_GRID_MODES));

export const canUseGridMoveMode = ({
  mode,
  status,
  actingAsNumberLineGetter,
  gridGuideOpen,
  gridGuideIgnoringMouseDown,
}: CanUseGridMoveModeOptions): boolean =>
  mode === GRID_MOVE_MODE &&
  status !== 'c' &&
  !actingAsNumberLineGetter &&
  !gridGuideOpen &&
  !gridGuideIgnoringMouseDown;

export const canUseGridAddControls = ({
  selected,
  status,
  moveModeActive,
  visible = true,
}: CanUseGridAddControlsOptions): boolean =>
  Boolean(selected && status !== 'c' && !moveModeActive && visible);

export const getGridControlCursor = ({
  enabled,
  moveModeActive,
}: {
  enabled: boolean;
  moveModeActive: boolean;
}): 'default' | 'pointer' =>
  enabled && !moveModeActive ? 'pointer' : 'default';

export const configureGridAddMoveMode = ({
  getter,
  getGridGuide,
  onAddModeActivated,
  paintCanvas,
}: ConfigureGridAddMoveModeOptions): void => {
  getter.gridAddMoveMode = GRID_ADD_MODE;

  getter.isGridMoveModeActive = function () {
    return getter.gridAddMoveMode === GRID_MOVE_MODE;
  };

  getter.canUseGridMoveMode = function () {
    const gridGuide = getGridGuide();

    return canUseGridMoveMode({
      mode: getter.gridAddMoveMode || GRID_ADD_MODE,
      status: getter.st,
      actingAsNumberLineGetter: getter.actingAsNumberLineGetter,
      gridGuideOpen: Boolean(gridGuide?.on),
      gridGuideIgnoringMouseDown: Boolean(gridGuide?.ignoreNextGridMouseDown),
    });
  };

  getter.setGridAddMoveMode = function (mode) {
    if (getter.gridAddMoveMode === mode) {
      return;
    }

    getter.gridAddMoveMode = mode;

    if (mode === GRID_ADD_MODE) {
      onAddModeActivated?.();
    }

    getter.updateUndoRedoButtons?.();
    paintCanvas();
  };
};
